import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaUserCog, FaSearch, FaPaperPlane, FaCheckCircle, FaStar, FaBolt, FaBuilding } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import { trackEvent } from '../utils/analytics';
import { RootState } from '../store';
import { getAccountAwarePlanCta } from '../utils/accountAwareCta';

const HomePage = () => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const automationCta = getAccountAwarePlanCta({
    isAuthenticated,
    role: user?.role,
    kind: 'worker-automation',
    signedOutLabel: 'Start Early Access — $9/mo',
    signedOutPath: '/register?role=worker&redirect=%2Fsubscription',
  });

  return (
    <div>
      <SEOHead
        title="StageCrew — Browse AV & Stagehand Gigs"
        description="Browse pay-disclosed AV technician, stagehand, DJ, and live-event production listings recently confirmed on employer-controlled sources."
        url="https://stagecrew.app"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'StageCrew',
          url: 'https://stagecrew.app',
          description: 'Job board and worker profiles for AV technicians, stagehands, DJs, and live-event crew.',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://stagecrew.app/jobs?search={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white bg-opacity-15 text-blue-100 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 backdrop-blur-sm">
            A focused job board built for AV &amp; stagehand crew
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Find Stagehand &amp;<br />DJ Job Listings
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-blue-100 max-w-3xl mx-auto">
            StageCrew shows pay-disclosed AV tech, stagehand, DJ, lighting, and audio opportunities that pass a current-source check.
            Review the employer listing, then apply on your terms.
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/register?role=worker"
              onClick={() => trackEvent('home_cta_click', 'conversion', 'hero_worker_profile')}
              className="bg-white text-blue-700 px-10 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              <FaUserCog className="text-blue-500" />
              Create Free Worker Profile
            </Link>
            <Link
              to="/for-employers"
              onClick={() => trackEvent('home_cta_click', 'conversion', 'hero_employer')}
              className="bg-white bg-opacity-10 border-2 border-white text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white hover:bg-opacity-20 transition inline-flex items-center justify-center gap-2"
            >
              <FaBuilding />
              Post a Gig
            </Link>
          </div>
          <p className="text-blue-200 text-sm">Free profile. Browse before signing up. No card required.</p>
          <Link
            to="/jobs"
            onClick={() => trackEvent('home_cta_click', 'engagement', 'hero_browse_gigs')}
            className="inline-block mt-4 text-white underline underline-offset-4 hover:text-blue-100 font-medium"
          >
            Browse listings before signing up
          </Link>
          <p className="mt-6 text-blue-100 text-sm max-w-3xl mx-auto leading-relaxed">
            StageCrew can complement direct relationships when you are entering the field, moving
            into a new market, or looking between calls. It organizes a smaller set of qualified listings;
            it does not replace union halls, production managers, vendors, or your professional network.
          </p>
        </div>
      </section>

      {/* Directory Search */}
      <section className="py-14 bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Find AV and live-event companies near you</h2>
          <p className="text-gray-500 mb-8">Browse AV and live-event companies across U.S. markets</p>
          <form
            className="flex gap-2 max-w-lg mx-auto mb-8"
            onSubmit={(e) => {
              e.preventDefault();
              const city = (e.currentTarget.elements.namedItem('city') as HTMLInputElement).value.trim();
              if (city) window.location.href = `/vendors?city=${encodeURIComponent(city)}`;
            }}
          >
            <label htmlFor="vendor-city-search" className="sr-only">City or market</label>
            <input
              id="vendor-city-search"
              name="city"
              type="text"
              placeholder="City (e.g. Los Angeles)"
              className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-blue-700 transition"
            >
              Search
            </button>
          </form>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'AV Rental', value: 'av_rental' },
              { label: 'Staging', value: 'staging' },
              { label: 'Lighting', value: 'lighting' },
              { label: 'Audio', value: 'audio' },
              { label: 'Production', value: 'production' },
              { label: 'Browse All', value: '' },
            ].map(({ label, value }) => (
              <a
                key={label}
                href={value ? `/vendors?specialty=${value}` : '/vendors'}
                className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-sm font-medium hover:border-blue-400 hover:text-blue-600 transition"
              >
                {label}
              </a>
            ))}
          </div>
          {/* California quick links */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Popular in California</p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                { label: 'Los Angeles AV Companies', href: '/vendors/city/los-angeles' },
                { label: 'Orange County AV Companies', href: '/vendors/city/orange-county' },
                { label: 'Inland Empire AV Companies', href: '/vendors/city/inland-empire' },
                { label: 'Anaheim AV Companies', href: '/vendors/city/anaheim' },
                { label: 'Burbank AV Companies', href: '/vendors/city/burbank' },
                { label: 'All California →', href: '/vendors?city=California' },
              ].map(({ label, href }) => (
                <a
                  key={href}
                  href={href}
                  className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline px-2 py-1"
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">How It Works</h2>
          <p className="text-center text-gray-600 mb-16 text-lg max-w-2xl mx-auto">
            Three simple steps from finding an opportunity to sending an application.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="bg-blue-100 text-blue-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaUserCog className="text-3xl" />
              </div>
              <div className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 1
              </div>
              <h3 className="text-xl font-bold mb-3">Create a Basic Profile</h3>
              <p className="text-gray-600">
                Add your primary role, city, and minimum rate.
                Certifications, schedule, and work links can wait.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 text-blue-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaSearch className="text-3xl" />
              </div>
              <div className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 2
              </div>
              <h3 className="text-xl font-bold mb-3">Browse Sourced Listings</h3>
              <p className="text-gray-600">
                Search live-event opportunities by market and role.
                Review the source, requirements, and application details.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 text-blue-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaPaperPlane className="text-3xl" />
              </div>
              <div className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 3
              </div>
              <h3 className="text-xl font-bold mb-3">Apply on Your Terms</h3>
              <p className="text-gray-600">
                Apply yourself for free, or use optional paid automation
                for supported postings when you are ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Prop */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">What You Can Do for Free</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="text-4xl font-bold text-blue-600 mb-3">Displayed</div>
              <div className="text-gray-700 font-medium">Crew Listings</div>
              <p className="text-gray-500 text-sm mt-2">Inventory changes as listings are added or closed</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="text-4xl font-bold text-blue-600 mb-3">Role + City</div>
              <div className="text-gray-700 font-medium">Preference Matching</div>
              <p className="text-gray-500 text-sm mt-2">Use profile details to surface more relevant opportunities</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="text-4xl font-bold text-blue-600 mb-3">$0</div>
              <div className="text-gray-700 font-medium">Manual Applications</div>
              <p className="text-gray-500 text-sm mt-2">Browse, save, match, and apply yourself without paying StageCrew</p>
            </div>
          </div>
        </div>
      </section>

      {/* Roles We Cover */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">Common Crew Roles</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Coverage varies by role, market, date, and source. Check the listings shown for your filters.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Gaffer', 'Best Boy', 'Lighting Tech', 'Key Grip', 'Grip', 'Rigger',
              'Stagehand', 'AV Technician', 'Sound Engineer', 'Camera Operator',
              'Production Assistant', 'Set Decorator', 'Prop Master', 'Wardrobe',
              'Makeup', 'Carpenter',
            ].map((role) => (
              <span
                key={role}
                className="bg-blue-50 text-blue-700 border border-blue-200 px-4 py-2 rounded-full text-sm font-medium"
              >
                {role}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Simple, Transparent Pricing</h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Browse and apply manually for free, or join automation early access for $9/month.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <h3 className="text-2xl font-bold mb-2">Free</h3>
              <div className="flex items-baseline mb-1">
                <span className="text-5xl font-bold">$0</span>
                <span className="text-gray-500 ml-2">core tools</span>
              </div>
              <p className="text-sm text-gray-600 font-medium mb-6">Core manual job-discovery tools</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Browse pay-disclosed, currently qualified listings</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Save jobs and work preferences</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Use in-app matching and apply manually</span>
                </li>
              </ul>
              <Link
                to="/jobs"
                className="block w-full text-center bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-bold hover:bg-gray-200 transition"
              >
                Browse Jobs Free
              </Link>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-blue-600">
              <h3 className="text-2xl font-bold mb-2">Automation Early Access</h3>
              <div className="flex items-baseline mb-1">
                <span className="text-5xl font-bold">$9</span>
                <span className="text-gray-500 ml-2">/month</span>
              </div>
              <p className="text-sm text-blue-600 font-medium mb-6">Optional automation while the feature matures</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Up to 20 supported automation attempts/week</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Review-first and pause controls</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Automatic submission only on supported destinations</span>
                </li>
              </ul>
              <Link
                to={automationCta.path}
                className="block w-full text-center bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition"
              >
                {automationCta.label}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">What StageCrew Helps You Organize</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaBolt className="text-3xl text-blue-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Pay &amp; Source Checks</h3>
              <p className="text-gray-600">
                Public listings include a concrete amount and pay period. External listings must also remain open
                on an approved employer-controlled source when StageCrew checks them.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaSearch className="text-3xl text-blue-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Preference Matching</h3>
              <p className="text-gray-600">
                Use your role, city, rate, certifications, and union status to prioritize listings.
                A match is not verification or a job guarantee.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <FaStar className="text-3xl text-blue-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Live-Event Focus</h3>
              <p className="text-gray-600">
                StageCrew filters for AV and live-event relevance, but workers should still check the employer,
                date, location, pay, and application path.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Employers Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-sm font-bold text-blue-600 uppercase tracking-wide mb-2">For Employers</div>
              <h2 className="text-4xl font-bold mb-6">Need Crew? Post a Gig.</h2>
              <p className="text-gray-600 text-lg mb-6">
                Publish the role, rate, schedule, and location in one place.
                Use the employer dashboard to manage the posting and find relevant crew.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  First 10 gig posts free — no credit card required
                </li>
                <li className="flex items-center text-gray-700">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  Clear role, rate, schedule, and location details
                </li>
                <li className="flex items-center text-gray-700">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  Search workers by role, location, certifications
                </li>
                <li className="flex items-center text-gray-700">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  Employer dashboard with analytics
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/for-employers"
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-blue-700 transition inline-block text-center"
                >
                  Learn More
                </Link>
                <Link
                  to="/register?role=employer"
                  className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-blue-50 transition inline-block text-center"
                >
                  Post a Gig Free
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-4">Example employer dashboard</p>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-semibold">Concert Load-In — Vegas</p>
                    <p className="text-sm text-gray-500">4 stagehands needed</p>
                  </div>
                  <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">Published</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-semibold">Corporate AV Setup — Orlando</p>
                    <p className="text-sm text-gray-500">2 AV techs needed</p>
                  </div>
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">Reviewing</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                  <div>
                    <p className="font-semibold">Film Lighting — Atlanta</p>
                    <p className="text-sm text-gray-500">1 gaffer, 2 grips</p>
                  </div>
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-3 py-1 rounded-full">Draft</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor Directory CTA */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-gray-200 p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-2">Vendor Directory</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Find Union Labor, AV Rental & More</h2>
              <p className="text-gray-500 max-w-xl">
                Browse listings for union labor, AV rental houses, backline providers, staging companies, and other live-event vendors. Claimed listings are marked so you can tell which businesses manage their own details.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/vendors"
                className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition text-center"
              >
                Browse Vendors
              </Link>
              <a
                href="mailto:edwin@stagecrew.app?subject=Vendor Directory Listing"
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition text-center"
              >
                List Your Business
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Browse Sourced Listings</h2>
          <p className="text-xl mb-10 text-blue-100 max-w-2xl mx-auto">
            Browse sourced listings for free, then create a basic profile for saved preferences and in-app matches.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register?role=worker"
              className="bg-white text-blue-700 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg inline-block"
            >
              Sign Up as Worker
            </Link>
            <Link
              to="/register?role=employer"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition inline-block"
            >
              Post a Gig as Employer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
